import React from 'react';

/**
 * StacksAndSubagentsDiagram
 *
 * A static structural diagram of Stackform's two-level agent architecture:
 *   - Stacks: the specialist agentic services (Orchestrator coordinates;
 *     Generalist / Researcher / Evaluator / Challenger each handle a kind of
 *     work).
 *   - Subagents: the three workers (Aeri / Capo / Cardian) that run inside
 *     EVERY stack's worker pool. You assign a model + persona to each once;
 *     the assignment applies across all stacks.
 */

const STACKS = [
  { name: 'Generalist', color: '#28a745', blurb: 'Real-time web & news' },
  { name: 'Researcher', color: '#7b1fa2', blurb: 'Academic literature' },
  { name: 'Evaluator', color: '#f59e0b', blurb: 'Audits answer quality' },
  { name: 'Challenger', color: '#e11d48', blurb: 'Stress-tests claims' },
];

// Each subagent's DEFAULT model family (you can reassign any of them).
const SUBAGENTS = [
  { name: 'Aeri', model: 'GPT (default)', color: '#0288d1' },
  { name: 'Capo', model: 'Gemini (default)', color: '#388e3c' },
  { name: 'Cardian', model: 'Claude (default)', color: '#7b1fa2' },
];

const card = {
  border: '1px solid var(--border-color, #ddd)',
  borderRadius: '8px',
  padding: '0.6rem 0.75rem',
  backgroundColor: 'var(--panel-background, #fff)',
};

const sectionLabel = {
  fontSize: '0.72rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--text-secondary, #888)',
  margin: '1.1rem 0 0.5rem',
};

const Avatar = ({ name, color }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
    backgroundColor: `${color}22`, color, fontWeight: 700, fontSize: '0.72rem',
  }}>
    {name[0]}
  </span>
);

const Arrow = () => (
  <div style={{
    textAlign: 'center', color: 'var(--text-secondary, #aaa)',
    lineHeight: 1, margin: '0.35rem 0', fontSize: '1.1rem',
  }}>
    ↓
  </div>
);

export default function StacksAndSubagentsDiagram() {
  return (
    <div style={{
      border: '1px solid var(--border-color, #e2e2e2)',
      borderRadius: '10px',
      padding: '1rem',
      margin: '1rem 0',
      backgroundColor: 'var(--bg-secondary, #fafafa)',
      fontSize: '0.85rem',
    }}>
      {/* Orchestrator (coordinator) */}
      <div style={{ ...card, borderLeft: '3px solid #636cff' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Avatar name="Orchestrator" color="#636cff" />
          <strong style={{ color: '#636cff' }}>Orchestrator</strong>
        </div>
        <div style={{ color: 'var(--text-secondary, #666)', marginTop: 4 }}>
          Coordinator — plans the work and routes it to the right stack.
        </div>
      </div>

      <Arrow />

      {/* Specialist stacks */}
      <div style={sectionLabel}>Specialist stacks</div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
        gap: '0.5rem',
      }}>
        {STACKS.map(s => (
          <div key={s.name} style={{ ...card, borderLeft: `3px solid ${s.color}` }}>
            <strong style={{ color: s.color }}>{s.name}</strong>
            <div style={{ color: 'var(--text-secondary, #666)', marginTop: 2 }}>
              {s.blurb}
            </div>
          </div>
        ))}
      </div>

      <Arrow />

      {/* Subagents inside every stack */}
      <div style={sectionLabel}>Inside every stack: your enabled subagents (up to 3)</div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
        gap: '0.5rem',
      }}>
        {SUBAGENTS.map(a => (
          <div key={a.name} style={{
            ...card,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <Avatar name={a.name} color={a.color} />
            <div>
              <strong style={{ color: a.color }}>{a.name}</strong>
              <div style={{ color: 'var(--text-secondary, #666)', fontSize: '0.78rem' }}>
                {a.model}
              </div>
            </div>
          </div>
        ))}
      </div>

      <p style={{
        margin: '0.85rem 0 0',
        fontSize: '0.8rem',
        color: 'var(--text-secondary, #777)',
        fontStyle: 'italic',
      }}>
        Aeri, Capo, and Cardian run in every stack's worker pool. Assign a model
        and persona to each once under Settings → Configurations → Agent Models —
        or set an agent's provider to None to disable it and run on just one or
        two. The assignment applies across all stacks, and you can add your own
        custom agents.
      </p>
    </div>
  );
}
